import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { wsBackend } from '../constants/api';
import { setNotify } from '../reducers/realtimeSlice';
import {
  notificationReceived,
  snapshotReceived,
} from '../reducers/notificationsSlice';

const RECONNECT_MS = 3000;
const RECONNECT_MAX_MS = 30000;

// El servidor cierra con este código cuando el token no vale o la cuenta no
// tiene usuario. Reintentar no sirve de nada y cada intento cuesta una consulta
// a la base de datos, así que se deja de reintentar.
const CLOSE_UNAUTHORIZED = 4401;

/**
 * Conexión en tiempo real al canal de avisos (`ws/notifications/`).
 *
 * Es el sustituto del listener de Firebase que encendía la campana de agendas:
 * el backend empuja un `agenda.notify` cuando se crea o reasigna una agenda
 * con el usuario.
 *
 * A diferencia de `useProgressSocket`, no hay respaldo por polling: el aviso es
 * simple y no se guarda en ninguna parte, así que no hay un estado del servidor
 * que se pueda recuperar por HTTP. Si el socket está caído, el aviso simplemente
 * no llega; lo que se garantiza es que en cuanto vuelve la conexión, esta
 * retoma sola.
 *
 * El token se lee de Redux (no de `localStorage`) para que un logout cierre el
 * socket en lugar de dejarlo reconectando en segundo plano.
 */
export default function useAgendaSocket() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.token);

  useEffect(() => {
    if (!token) return undefined;

    let socket = null;
    let stopped = false;
    let reconnectTimer = null;
    let attempts = 0;

    const connect = () => {
      if (stopped) return;
      // `down` marca que ESTA conexión ya se cayó. Sin él, `onerror` y
      // `onclose` (que llegan juntos en un handshake rechazado) programan dos
      // reconexiones y el número de sockets se multiplica en cada intento.
      let down = false;

      socket = new WebSocket(
        `${wsBackend}/ws/notifications/?token=${encodeURIComponent(token)}`
      );

      socket.onopen = () => {
        attempts = 0;
      };

      socket.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch (e) {
          return;
        }
        if (!payload) return;

        // Aviso en vivo: alguien te asignó una agenda ahora mismo.
        if (payload.type === 'agenda.notify') {
          dispatch(setNotify(true));
        } else if (payload.type === 'agenda.snapshot') {
          // Lo pendiente al abrir la app: cubre los avisos que se perdieron
          // mientras no había conexión.
          if (payload.count > 0) dispatch(setNotify(true));
        } else if (payload.type === 'notification.new' && payload.notification) {
          // Notificación persistida (agenda o anuncio): alimenta la bandeja,
          // el contador de la campana y la cola del modal tipo push.
          dispatch(notificationReceived(payload.notification));
        } else if (payload.type === 'notifications.snapshot') {
          // Lo no leído que quedó en la base al abrir la app.
          dispatch(snapshotReceived({ unreadCount: payload.unread_count }));
        }
      };

      const handleDown = (event) => {
        if (down || stopped) return;
        down = true;

        const failed = socket;
        socket = null;
        if (failed) {
          try {
            failed.close();
          } catch (e) {
            // Ya estaba cerrado: no hay nada que liberar.
          }
        }

        if (event && event.code === CLOSE_UNAUTHORIZED) {
          // El token ya no vale: se deja de reintentar hasta el próximo login.
          stopped = true;
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }

        // CLOSE_ABNORMAL (1006) y cualquier otro corte: backoff. El tope de
        // 30 s mantiene el reintento barato aunque el servidor esté caído.
        if (reconnectTimer) clearTimeout(reconnectTimer);
        const delay = Math.min(RECONNECT_MS * Math.pow(2, attempts), RECONNECT_MAX_MS);
        attempts += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.onclose = handleDown;
      socket.onerror = handleDown;
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, [token, dispatch]);
}